import { useTranslation } from 'react-i18next';
import { Dialog } from '@/components/ui/organisms/Dialog';
import { Button } from '@/components/ui/atoms/Button';
import { getGTMAttributes } from '@/utils/gtm/gtm-tracking';
import {
  getConfirmationDialogButtonTracking,
  getConfirmationDialogCloseTracking,
} from '@/utils/gtm/gtm-confirmation-dialog';
import { CandidateIdentity } from './CandidateIdentity';

interface HireCandidateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  candidateName?: string;
  candidateEmail?: string | null;
  isPending: boolean;
  origin: string;
}

export function HireCandidateDialog({
  open,
  onClose,
  onConfirm,
  candidateName,
  candidateEmail,
  isPending,
  origin,
}: HireCandidateDialogProps) {
  const { t } = useTranslation('candidates');
  const gtmConfig = { gtm: 'HIRE_CANDIDATE' as const, origin };
  const title = t('meta_cv.hire_dialog.title');

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      title={title}
    >
      <Dialog.Header
        titleVariant="h3"
        title={title}
        showCloseButton
        closeButtonProps={getGTMAttributes(getConfirmationDialogCloseTracking(gtmConfig))}
      />
      <Dialog.Body>
        <CandidateIdentity
          name={candidateName?.trim() || t('meta_cv.hire_dialog.candidate_fallback')}
          email={candidateEmail}
        />
      </Dialog.Body>
      <Dialog.Footer align="right">
        <Button
          {...getGTMAttributes(
            getConfirmationDialogButtonTracking(gtmConfig, {
              intent: 'secondary',
              isLastButton: false,
            })
          )}
          type="outlined"
          intent="secondary"
          onClick={onClose}
          disabled={isPending}
          label={t('meta_cv.hire_dialog.cancel')}
        />
        <Button
          {...getGTMAttributes(
            getConfirmationDialogButtonTracking(gtmConfig, {
              intent: 'secondary',
              isLastButton: true,
            })
          )}
          type="contained"
          intent="secondary"
          onClick={onConfirm}
          disabled={isPending}
          loading={isPending}
          label={t('meta_cv.hire_dialog.confirm')}
        />
      </Dialog.Footer>
    </Dialog>
  );
}
